import type { BetterFetch } from "@better-fetch/fetch";
import type { Store } from "@di-devs/better-feature/types";
import { atom } from "nanostores";
import { memberCoreEndpointContract } from "../../shared/contracts/memberCore.contracts";
import type { Member } from "../../shared/types/memberCore.types";
import type {
	BrowseMembers200Response,
	BrowseMembersQueryParams,
	DeleteMember200Response,
	GenerateMemberCSV200Response,
	GenerateMemberCSVRequest,
	GetMember200Response,
	ListMembers200Response,
	UpdateMember200Response,
	UpdateMemberRequest,
} from "../../shared/types/memberCoreApi.types";
import {
	createQuerySetMap,
	extractListeners,
	extractQueries,
	extractSignals,
} from "../shared/queryFactory";

export function createMemberCorePlugin($fetch: BetterFetch, _$store?: Store) {
	const querySets = createQuerySetMap(memberCoreEndpointContract, $fetch);

	// Define atoms
	const $members = atom<Member[]>([]);
	const $selectedMember = atom<Member | null>(null);
	const $isLoading = atom<boolean>(false);
	const $error = atom<string | null>(null);
	// const $pagination = atom<PaginationParams>({
	// 	page: 1,
	// 	limit: 20,
	// 	totalCount: 0,
	// 	maxPages: 1,
	// });

	return {
		signals: extractSignals(querySets),
		queries: extractQueries(querySets),
		listeners: extractListeners(querySets),
		atoms: {
			$members,
			$selectedMember,
			$isLoading,
			$error,
			// $pagination,
		},
		actions: {
			async browseMembers(params?: BrowseMembersQueryParams) {
				$isLoading.set(true);
				$error.set(null);

				try {
					// Clean up keys with 'Filter' in the name
					const cleanFilterParams: { [key: string]: any } = {};
					for (const [key, value] of Object.entries(params || {})) {
						const cleanKeyName = key.replace("Filter", "");
						cleanFilterParams[cleanKeyName as string] = value;
					}

					const queryParams = new URLSearchParams();
					if (params) {
						Object.entries(params).forEach(([key, value]) => {
							if (value !== undefined && value !== null) {
								const cleanKeyName = key.replace("Filter", "");
								queryParams.append(cleanKeyName, String(value));
							}
						});
					}

					const result = await $fetch<BrowseMembers200Response>(
						`${memberCoreEndpointContract.browseMembers.path}${queryParams.toString() ? `?${queryParams.toString()}` : ""}`,
						{
							method: memberCoreEndpointContract.browseMembers.method,
						},
					);

					// if (result?.data) {
					// 	$members.set(result.data.members || []);
					// 	if (result.data.pagination) {
					// 		$pagination.set(result.data.pagination);
					// 	}
					// }

					return result;
				} catch (error) {
					const errorMessage =
						error instanceof Error ? error.message : "Failed to browse members";
					$error.set(errorMessage);
					throw error;
				} finally {
					$isLoading.set(false);
				}
			},

			async listMembers() {
				$isLoading.set(true);
				$error.set(null);

				try {
					const result = await $fetch<ListMembers200Response>(
						memberCoreEndpointContract.listMembers.path,
						{
							method: memberCoreEndpointContract.listMembers.method,
						},
					);

					// if (result?.data) {
					// 	$members.set(result.data.members || []);
					// }

					return result;
				} catch (error) {
					const errorMessage =
						error instanceof Error ? error.message : "Failed to list members";
					$error.set(errorMessage);
					throw error;
				} finally {
					$isLoading.set(false);
				}
			},

			async getMember(memberId: Member["memberId"]) {
				$isLoading.set(true);
				$error.set(null);

				try {
					const result = await $fetch<GetMember200Response>(
						memberCoreEndpointContract.getMember.path.replace(
							":memberId",
							String(memberId),
						),
						{
							method: memberCoreEndpointContract.getMember.method,
						},
					);

					// if (result?.data?.member) {
					// 	$selectedMember.set(result.data.member);
					// }

					return result;
				} catch (error) {
					const errorMessage =
						error instanceof Error ? error.message : "Failed to get member";
					$error.set(errorMessage);
					throw error;
				} finally {
					$isLoading.set(false);
				}
			},

			async updateMember(
				memberId: Member["memberId"],
				data: UpdateMemberRequest,
			) {
				$isLoading.set(true);
				$error.set(null);

				try {
					const result = await $fetch<UpdateMember200Response>(
						memberCoreEndpointContract.updateMember.path.replace(
							":memberId",
							String(memberId),
						),
						{
							method: memberCoreEndpointContract.updateMember.method,
							body: data,
						},
					);

					// Update the selected member if it's the one being updated
					// const currentSelected = $selectedMember.get();
					// if (currentSelected && currentSelected.memberId === Number(memberId)) {
					// 	if (result?.data?.member) {
					// 		$selectedMember.set(result.data.member);
					// 	}
					// }

					// TODO: Investigate atomic invalidation and caching here vs. query cache
					// Refresh the list after update
					// await this.browseMembers();

					return result;
				} catch (error) {
					const errorMessage =
						error instanceof Error ? error.message : "Failed to update member";
					$error.set(errorMessage);
					throw error;
				} finally {
					$isLoading.set(false);
				}
			},

			async deleteMember(memberId: Member["memberId"]) {
				$isLoading.set(true);
				$error.set(null);

				try {
					const result = await $fetch<DeleteMember200Response>(
						memberCoreEndpointContract.deleteMember.path.replace(
							":memberId",
							String(memberId),
						),
						{
							method: memberCoreEndpointContract.deleteMember.method,
						},
					);

					// Clear selected member if it's the one being deleted
					const currentSelected = $selectedMember.get();
					if (
						currentSelected &&
						currentSelected.memberId === Number(memberId)
					) {
						$selectedMember.set(null);
					}

					// Remove the deleted member from the list
					const currentMembers = $members.get();
					const updatedMembers = currentMembers.filter(
						(member) => member.memberId !== Number(memberId),
					);
					$members.set(updatedMembers);

					return result;
				} catch (error) {
					const errorMessage =
						error instanceof Error ? error.message : "Failed to delete member";
					$error.set(errorMessage);
					throw error;
				} finally {
					$isLoading.set(false);
				}
			},

			async generateMemberCSV(data: GenerateMemberCSVRequest) {
				$isLoading.set(true);
				$error.set(null);

				try {
					const result = await $fetch<GenerateMemberCSV200Response>(
						memberCoreEndpointContract.generateMemberCSV.path,
						{
							method: memberCoreEndpointContract.generateMemberCSV.method,
							body: data,
						},
					);

					return result;
				} catch (error) {
					const errorMessage =
						error instanceof Error ? error.message : "Failed to generate CSV";
					$error.set(errorMessage);
					throw error;
				} finally {
					$isLoading.set(false);
				}
			},

			selectMember(member: Member | null) {
				$selectedMember.set(member);
			},

			setLoading(isLoading: boolean) {
				$isLoading.set(isLoading);
			},

			setError(error: string | null) {
				$error.set(error);
			},

			// setPagination(pagination: Partial<PaginationParams>) {
			// 	const current = $pagination.get();
			// 	$pagination.set({ ...current, ...pagination });
			// },
		},
		querySets, // Expose the raw querySets with proper typing
	};
}
